import type { FestivalConfig, FestivalWeekend } from "./types";
import { typicalWindowForYear } from "./windows";
import { isFestivalEditionAvailable } from "./availability";

export type FestivalListing = {
  festival: FestivalConfig;
  year: number;
  confirmed: boolean;
  windows: FestivalWeekend[];
};

/** Inclusive calendar dates, using the same edition-year convention as weather. */
export function festivalListings(
  festivals: readonly FestivalConfig[],
  from: string,
  to: string,
): FestivalListing[] {
  const firstYear = Number(from.slice(0, 4)) - 1;
  const lastYear = Number(to.slice(0, 4)) + 1;
  const listings: FestivalListing[] = [];

  for (const festival of festivals) {
    for (let year = firstYear; year <= lastYear; year++) {
      if (!isFestivalEditionAvailable(festival, year)) continue;
      const confirmed = festival.confirmedDates?.find((dates) => dates.year === year);
      const typical = typicalWindowForYear(festival, year);
      const windows = (confirmed?.weekends?.length
        ? confirmed.weekends
        : [{
            label: "",
            start: confirmed?.start ?? typical.start.toISOString().slice(0, 10),
            end: confirmed?.end ?? typical.end.toISOString().slice(0, 10),
          }]
      )
        .filter((window) => window.end >= from && window.start <= to)
        .sort((a, b) => a.start.localeCompare(b.start));

      if (windows.length) {
        listings.push({ festival, year, confirmed: Boolean(confirmed), windows });
      }
    }
  }

  return listings.sort((a, b) =>
    a.windows[0].start.localeCompare(b.windows[0].start)
    || a.festival.name.localeCompare(b.festival.name),
  );
}

/** One next edition per festival, including events still running today. */
export function upcomingFestivalListings(
  festivals: readonly FestivalConfig[],
  today: string,
): FestivalListing[] {
  const end = new Date(`${today}T00:00:00Z`);
  end.setUTCFullYear(end.getUTCFullYear() + 1);
  const seen = new Set<string>();
  return festivalListings(festivals, today, end.toISOString().slice(0, 10))
    .filter(({ festival }) => {
      if (seen.has(festival.slug)) return false;
      seen.add(festival.slug);
      return true;
    });
}

export function festivalDateLabel(start: string, end: string): string {
  const startDate = new Date(`${start}T00:00:00Z`);
  const endDate = new Date(`${end}T00:00:00Z`);
  const format = (date: Date, year = false) => date.toLocaleDateString("en-GB", {
    day: "numeric", month: "short", ...(year ? { year: "numeric" } : {}), timeZone: "UTC",
  });
  if (start === end) return format(startDate, true);
  if (start.slice(0, 7) === end.slice(0, 7)) {
    return `${startDate.getUTCDate()} to ${format(endDate, true)}`;
  }
  return `${format(startDate, start.slice(0, 4) !== end.slice(0, 4))} to ${format(endDate, true)}`;
}
